import { ReactElement, useMemo, useCallback } from 'react';
import { useFormik, FormikHelpers, FormikProvider } from 'formik';
import {
  Stack,
  Typography,
  FormControlLabel,
  Switch,
  TextField,
  MenuItem,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';

import { hasError } from '~utils/formHelpers';

import {
  ConfigBillingListFormProps,
  ConfigBillingListFormValues,
} from './types';
import { FIELDS_NAME, getInitialValues, validationSchema } from './utils';

const ConfigBillingListForm = ({
  onSubmit,
  loadingSubmit = false,
  data,
  type = 'create',
  valuationTypesOptions,
}: ConfigBillingListFormProps): ReactElement => {
  const isCreate = useMemo(() => type === 'create', [type]);

  const executeSubmit = useCallback(
    async (
      values: ConfigBillingListFormValues,
      { setSubmitting }: FormikHelpers<ConfigBillingListFormValues>,
    ) => {
      await onSubmit(values);

      setSubmitting(false);
    },
    [onSubmit],
  );

  const formikBag = useFormik({
    initialValues: getInitialValues(data),
    validationSchema,
    onSubmit: executeSubmit,
  });

  const {
    errors,
    touched,
    getFieldProps,
    handleSubmit,
    values,
    isSubmitting,
    dirty,
  } = formikBag;

  return (
    <FormikProvider value={formikBag}>
      <Stack spacing={2} component="form" onSubmit={handleSubmit}>
        <Stack spacing={2}>
          <Stack spacing={2}>
            <Typography component="h6" fontWeight="bold">
              Detalle de lista de precios
            </Typography>

            <FormControlLabel
              control={
                <Switch
                  {...getFieldProps(FIELDS_NAME.ENABLED)}
                  checked={values[FIELDS_NAME.ENABLED]}
                />
              }
              label="Activo"
              sx={{ maxWidth: 'max-content' }}
              disabled={isCreate}
            />

            <Stack direction="row" spacing={2}>
              <TextField
                label="Código de lista de precios"
                autoComplete="off"
                fullWidth
                error={hasError(touched, errors, FIELDS_NAME.CODE)}
                {...getFieldProps(FIELDS_NAME.CODE)}
                sx={{ maxWidth: 300 }}
              />

              <TextField
                label="Descripción"
                autoComplete="off"
                fullWidth
                error={hasError(touched, errors, FIELDS_NAME.DESCRIPTION)}
                {...getFieldProps(FIELDS_NAME.DESCRIPTION)}
              />

              <TextField
                label="Plazo máximo entrega (días hábiles)"
                fullWidth
                autoComplete="off"
                type="number"
                error={hasError(touched, errors, FIELDS_NAME.MAX_DELIVERY_DAYS)}
                {...getFieldProps(FIELDS_NAME.MAX_DELIVERY_DAYS)}
                sx={{ maxWidth: 250 }}
              />

              <TextField
                label="Tipo de valorización"
                fullWidth
                autoComplete="off"
                error={hasError(touched, errors, FIELDS_NAME.VALUATION_TYPE_ID)}
                {...getFieldProps(FIELDS_NAME.VALUATION_TYPE_ID)}
                select
                sx={{ maxWidth: 200 }}
              >
                {valuationTypesOptions.map((valuationTypeOption) => (
                  <MenuItem
                    key={valuationTypeOption.value}
                    value={String(valuationTypeOption.value)}
                  >
                    {valuationTypeOption.label}
                  </MenuItem>
                ))}
              </TextField>
            </Stack>
          </Stack>
        </Stack>

        <Stack direction="row" spacing={2}>
          <LoadingButton
            type="submit"
            variant="contained"
            color="primary"
            loading={loadingSubmit || isSubmitting}
            disabled={!isCreate && !dirty}
            sx={{ minWidth: 200 }}
          >
            {isCreate ? 'Crear' : 'Guardar'}
          </LoadingButton>
        </Stack>
      </Stack>
    </FormikProvider>
  );
};

export default ConfigBillingListForm;
